import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import ExportButtons from "@/components/app/ExportButtons";
import { AlertTriangle, TrendingUp, TrendingDown, Fuel, Gauge, Calculator, Plus, Trash2, Save, Loader2, ChevronDown, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar, Cell,
} from "recharts";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

const alertStyles = {
  critical: { bg: "bg-[#DC2626]", text: "text-white", border: "border-[#DC2626]" },
  warning: { bg: "bg-[#F59E0B]", text: "text-white", border: "border-[#F59E0B]" },
  info: { bg: "bg-[#0020C2]", text: "text-white", border: "border-[#0020C2]" },
  stable: { bg: "bg-neutral-100", text: "text-neutral-700", border: "border-neutral-200" },
};

const trDate = (d) => (d ? d.split("-").reverse().join(".") : "-");

// Zam Hesaplayıcı: firma + çoklu güzergah, kaydet & firma bazlı indir
const ZamCalculator = ({ onSaved }) => {
  const [oldDate, setOldDate] = useState("");
  const [newDate, setNewDate] = useState("");
  const [oldPrice, setOldPrice] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [fuelShare, setFuelShare] = useState("40");
  const [companyName, setCompanyName] = useState("");
  const [routes, setRoutes] = useState([{ name: "", price: "" }]);
  const [saving, setSaving] = useState(false);

  const op = parseFloat(oldPrice) || 0;
  const np = parseFloat(newPrice) || 0;
  const share = parseFloat(fuelShare) || 0;
  const increasePct = op > 0 && np > 0 ? ((np - op) / op) * 100 : null;

  const calcRoute = (price) => {
    const x = parseFloat(price) || 0;
    if (increasePct == null || x <= 0) return null;
    const zam = x * (share / 100) * (increasePct / 100);
    return { zam, newPrice: x + zam };
  };

  const validRoutes = routes.filter((r) => r.name.trim() && parseFloat(r.price) > 0);
  const totals = validRoutes.reduce((acc, r) => {
    const c = calcRoute(r.price);
    if (c) { acc.old += parseFloat(r.price); acc.zam += c.zam; acc.new += c.newPrice; }
    return acc;
  }, { old: 0, zam: 0, new: 0 });

  const save = async () => {
    if (!companyName.trim()) { toast.error("Firma adı girin"); return; }
    if (increasePct == null) { toast.error("Eski ve yeni yakıt fiyatlarını girin"); return; }
    if (validRoutes.length === 0) { toast.error("En az bir güzergah (ad + fiyat) ekleyin"); return; }
    setSaving(true);
    try {
      await api.post("/fuel-impact/zam-records", {
        company_name: companyName.trim(),
        old_date: oldDate, new_date: newDate,
        old_price: op, new_price: np, fuel_share: share,
        routes: validRoutes.map((r) => ({ name: r.name.trim(), price: parseFloat(r.price) })),
      });
      toast.success(`"${companyName.trim()}" için zam kaydı oluşturuldu`);
      setCompanyName("");
      setRoutes([{ name: "", price: "" }]);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const inputCls = "mt-1 w-full px-2.5 py-2 bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none text-sm num";
  const lbl = "text-[10px] uppercase tracking-widest text-neutral-500 font-semibold";

  return (
    <div className="bg-white border border-[#E5E5E5] border-l-4 border-l-[#0020C2] rounded-md mb-6" data-testid="zam-calculator">
      <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
        <Calculator className="w-4 h-4 text-[#0020C2]" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Zam Hesaplayıcı</div>
          <div className="text-lg font-bold font-heading mt-0.5">Firma & Güzergah Bazlı Zam Hesabı</div>
        </div>
      </div>
      <div className="p-5 space-y-5">
        {/* ADIM 1: Yakıt artış oranı */}
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700 mb-3">1. Yakıt Artış Oranı</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className={lbl}>Eski Tarih</label>
              <input data-testid="calc-old-date" type="date" value={oldDate} onChange={(e) => setOldDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={lbl}>Eski Fiyat (₺/Lt)</label>
              <input data-testid="calc-old-price" type="number" step="0.01" placeholder="65,65" value={oldPrice} onChange={(e) => setOldPrice(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={lbl}>Yeni Tarih</label>
              <input data-testid="calc-new-date" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={lbl}>Yeni Fiyat (₺/Lt)</label>
              <input data-testid="calc-new-price" type="number" step="0.01" placeholder="78,82" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className={inputCls} />
            </div>
            <div className={`p-2 border text-center ${increasePct != null ? "bg-[#FEF2F2] border-[#FECACA]" : "bg-[#FAFAFA] border-[#F5F5F5]"}`}>
              <div className={lbl}>Artış Oranı</div>
              <div className={`num font-bold text-xl ${increasePct != null ? "text-[#DC2626]" : "text-neutral-300"}`} data-testid="calc-increase-pct">
                {increasePct != null ? `%${increasePct.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}` : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* ADIM 2: Firma + güzergahlar */}
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700 mb-3">2. Firma ve Güzergahlar</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={lbl}>Firma Adı *</label>
              <input data-testid="calc-company-name" type="text" placeholder="örn: Yıldız Tekstil A.Ş." value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputCls.replace(" num", "")} />
            </div>
            <div>
              <label className={lbl}>Yakıt Payı (%)</label>
              <input data-testid="calc-fuel-share" type="number" step="1" min="0" max="100" value={fuelShare} onChange={(e) => setFuelShare(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="border border-[#E5E5E5]">
            <div className="grid grid-cols-[1fr_130px_120px_130px_40px] gap-2 px-3 py-2 bg-[#FAFAFA] text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
              <span>Güzergah</span><span className="text-right">Günlük Fiyat (₺)</span><span className="text-right">Zam</span><span className="text-right">Zamlı Fiyat</span><span></span>
            </div>
            {routes.map((r, i) => {
              const c = calcRoute(r.price);
              return (
                <div key={i} className="grid grid-cols-[1fr_130px_120px_130px_40px] gap-2 px-3 py-2 border-t border-[#F5F5F5] items-center">
                  <input data-testid={`calc-route-name-${i}`} type="text" placeholder={`Güzergah ${i + 1}`} value={r.name}
                         onChange={(e) => setRoutes(routes.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                         className="px-2 py-1.5 bg-white border border-[#E5E5E5] text-sm" />
                  <input data-testid={`calc-route-price-${i}`} type="number" step="0.01" placeholder="1.000" value={r.price}
                         onChange={(e) => setRoutes(routes.map((x, j) => j === i ? { ...x, price: e.target.value } : x))}
                         className="px-2 py-1.5 bg-white border border-[#E5E5E5] text-sm num text-right" />
                  <span className="text-right num text-sm text-[#DC2626]">{c ? `+${formatTL(c.zam)}` : "—"}</span>
                  <span className="text-right num text-sm font-bold">{c ? formatTL(c.newPrice) : "—"}</span>
                  <button data-testid={`calc-route-remove-${i}`} onClick={() => setRoutes(routes.length > 1 ? routes.filter((_, j) => j !== i) : [{ name: "", price: "" }])} className="p-1 hover:bg-[#DC2626] hover:text-white justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
            <button data-testid="calc-add-route" onClick={() => setRoutes([...routes, { name: "", price: "" }])}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border-t border-[#F5F5F5] text-[10px] uppercase tracking-widest font-bold text-[#0020C2] hover:bg-[#F5F8FF]">
              <Plus className="w-3.5 h-3.5" /> Güzergah Ekle
            </button>
          </div>

          {validRoutes.length > 0 && increasePct != null && (
            <div className="mt-3 flex items-center justify-between px-4 py-3 bg-[#0020C2] text-white" data-testid="calc-totals">
              <span className="text-[11px] uppercase tracking-widest font-bold">{validRoutes.length} Güzergah · Toplam</span>
              <span className="num text-sm">{formatTL(totals.old)} <span className="opacity-70">→ zam</span> +{formatTL(totals.zam)} <span className="opacity-70">→</span> <b className="text-lg">{formatTL(totals.new)}</b></span>
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <button data-testid="calc-save-btn" onClick={save} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sisteme Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Kayıtlı zam hesabını düzenleme — fiyat değişince zam aynı formülle anında yeniden hesaplanır
const ZamRecordEdit = ({ record, onSaved, onCancel }) => {
  const [draft, setDraft] = useState({
    old_date: record.old_date || "", new_date: record.new_date || "",
    old_price: String(record.old_price), new_price: String(record.new_price),
    fuel_share: String(record.fuel_share),
    routes: record.routes.map(rt => ({ name: rt.name, price: String(rt.price) })),
  });
  const [saving, setSaving] = useState(false);

  const op = parseFloat(draft.old_price) || 0;
  const np = parseFloat(draft.new_price) || 0;
  const share = parseFloat(draft.fuel_share) || 0;
  const pct = op > 0 && np > 0 ? ((np - op) / op) * 100 : null;
  const calc = (price) => {
    const x = parseFloat(price) || 0;
    if (pct == null || x <= 0) return null;
    const zam = x * (share / 100) * (pct / 100);
    return { zam, np: x + zam };
  };
  const valid = draft.routes.filter(rt => rt.name.trim() && parseFloat(rt.price) > 0);
  const totals = valid.reduce((a, rt) => {
    const c = calc(rt.price);
    if (c) { a.old += parseFloat(rt.price); a.zam += c.zam; a.new += c.np; }
    return a;
  }, { old: 0, zam: 0, new: 0 });
  const setRoute = (i, k, v) => setDraft(d => ({ ...d, routes: d.routes.map((x, j) => j === i ? { ...x, [k]: v } : x) }));

  const save = async () => {
    if (pct == null) { toast.error("Eski ve yeni yakıt fiyatlarını kontrol edin"); return; }
    if (valid.length === 0) { toast.error("En az bir güzergah (ad + fiyat) gerekli"); return; }
    setSaving(true);
    try {
      await api.put(`/fuel-impact/zam-records/${record.id}`, {
        company_name: record.company_name,
        old_date: draft.old_date, new_date: draft.new_date,
        old_price: op, new_price: np, fuel_share: share,
        routes: valid.map(rt => ({ name: rt.name.trim(), price: parseFloat(rt.price) })),
      });
      toast.success("Zam kaydı güncellendi — zamlar yeniden hesaplandı");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const inp = "px-2 py-1.5 bg-white border border-[#E5E5E5] focus:border-[#0020C2] focus:outline-none text-xs num w-full";
  const lbl = "text-[9px] uppercase tracking-widest text-neutral-500 font-semibold";

  return (
    <div className="px-5 pb-4" data-testid={`zam-record-edit-${record.id}`}>
      <div className="border border-[#0020C2] bg-[#F5F8FF] p-3 space-y-3">
        {/* Yakıt fiyatları + pay */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 items-end">
          <div><div className={lbl}>Eski Tarih</div><input type="date" data-testid={`zam-edit-old-date-${record.id}`} value={draft.old_date} onChange={e => setDraft(d => ({ ...d, old_date: e.target.value }))} className={inp} /></div>
          <div><div className={lbl}>Eski Yakıt (₺/Lt)</div><input type="number" step="0.01" data-testid={`zam-edit-old-price-${record.id}`} value={draft.old_price} onChange={e => setDraft(d => ({ ...d, old_price: e.target.value }))} className={inp} /></div>
          <div><div className={lbl}>Yeni Tarih</div><input type="date" data-testid={`zam-edit-new-date-${record.id}`} value={draft.new_date} onChange={e => setDraft(d => ({ ...d, new_date: e.target.value }))} className={inp} /></div>
          <div><div className={lbl}>Yeni Yakıt (₺/Lt)</div><input type="number" step="0.01" data-testid={`zam-edit-new-price-${record.id}`} value={draft.new_price} onChange={e => setDraft(d => ({ ...d, new_price: e.target.value }))} className={inp} /></div>
          <div><div className={lbl}>Yakıt Payı (%)</div><input type="number" step="1" min="0" max="100" data-testid={`zam-edit-fuel-share-${record.id}`} value={draft.fuel_share} onChange={e => setDraft(d => ({ ...d, fuel_share: e.target.value }))} className={inp} /></div>
          <div className="text-center border border-[#FECACA] bg-[#FEF2F2] py-1">
            <div className={lbl}>Artış</div>
            <div className="num font-bold text-sm text-[#DC2626]" data-testid={`zam-edit-pct-${record.id}`}>{pct != null ? `%${pct.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}` : "—"}</div>
          </div>
        </div>

        {/* Güzergahlar — düzenlenebilir */}
        <table className="w-full text-xs border border-[#E5E5E5] bg-white">
          <thead className="bg-[#FAFAFA]">
            <tr>{["Güzergah", "Günlük Fiyat", "Zam", "Zamlı Fiyat", ""].map((h, i) => (
              <th key={i} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 1 && i <= 3 ? "text-right" : "text-left"}`}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {draft.routes.map((rt, i) => {
              const c = calc(rt.price);
              return (
                <tr key={i} className="border-t border-[#F5F5F5]">
                  <td className="px-2 py-1.5"><input data-testid={`zam-edit-route-name-${record.id}-${i}`} value={rt.name} onChange={e => setRoute(i, "name", e.target.value)} className={inp.replace(" num", "")} /></td>
                  <td className="px-2 py-1.5 w-[130px]"><input type="number" step="0.01" data-testid={`zam-edit-route-price-${record.id}-${i}`} value={rt.price} onChange={e => setRoute(i, "price", e.target.value)} className={`${inp} text-right`} /></td>
                  <td className="px-3 py-1.5 text-right num text-[#DC2626] w-[110px]" data-testid={`zam-edit-route-zam-${record.id}-${i}`}>{c ? `+${formatTL(c.zam)}` : "—"}</td>
                  <td className="px-3 py-1.5 text-right num font-bold w-[130px]">{c ? formatTL(c.np) : "—"}</td>
                  <td className="px-2 py-1.5 w-[36px]">
                    <button data-testid={`zam-edit-route-remove-${record.id}-${i}`} onClick={() => setDraft(d => ({ ...d, routes: d.routes.filter((_, j) => j !== i) }))} disabled={draft.routes.length <= 1} className="p-1 hover:bg-[#DC2626] hover:text-white disabled:opacity-30"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-[#E5E5E5] bg-[#FAFAFA] font-bold">
              <td className="px-3 py-2 text-[10px] uppercase tracking-widest">Toplam</td>
              <td className="px-3 py-2 text-right num">{formatTL(totals.old)}</td>
              <td className="px-3 py-2 text-right num text-[#DC2626]">+{formatTL(totals.zam)}</td>
              <td className="px-3 py-2 text-right num" data-testid={`zam-edit-total-new-${record.id}`}>{formatTL(totals.new)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div className="flex items-center justify-between">
          <button data-testid={`zam-edit-add-route-${record.id}`} onClick={() => setDraft(d => ({ ...d, routes: [...d.routes, { name: "", price: "" }] }))} className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#0020C2] hover:underline">
            <Plus className="w-3 h-3" /> Güzergah Ekle
          </button>
          <div className="flex gap-2">
            <button data-testid={`zam-edit-cancel-${record.id}`} onClick={onCancel} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-white">Vazgeç</button>
            <button data-testid={`zam-edit-save-${record.id}`} onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Kayıtlı zam hesapları: firma bazlı listele + indir + sil
const ZamRecords = ({ records, onDeleted }) => {
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(null);

  const del = async (r) => {
    if (!window.confirm(`"${r.company_name}" zam kaydı silinsin mi?`)) return;
    try { await api.delete(`/fuel-impact/zam-records/${r.id}`); toast.success("Silindi"); onDeleted(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (records.length === 0) return null;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden mb-6" data-testid="zam-records">
      <div className="px-5 py-4 border-b border-[#E5E5E5]">
        <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Kayıtlı Zam Hesapları</div>
        <div className="text-lg font-bold font-heading mt-1">Firma Bazlı Zam Geçmişi ({records.length})</div>
      </div>
      <div className="divide-y divide-[#F5F5F5]">
        {records.map((r) => (
          <div key={r.id} data-testid={`zam-record-${r.id}`}>
            <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_180px_110px_140px_auto] gap-3 items-center px-5 py-3">
              <div>
                <div className="font-semibold text-sm">{r.company_name}</div>
                <div className="text-[11px] text-neutral-500 num">{trDate(r.old_date)} → {trDate(r.new_date)} · {r.routes.length} güzergah</div>
              </div>
              <div className="hidden md:block text-right">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500">Yakıt</div>
                <div className="num text-xs font-semibold">{formatTL(r.old_price)} → {formatTL(r.new_price)}</div>
              </div>
              <div className="hidden md:block text-right">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500">Artış</div>
                <div className="num text-sm font-bold text-[#DC2626]">%{formatNumber(r.increase_pct, 2)}</div>
              </div>
              <div className="hidden md:block text-right">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500">Toplam Zamlı</div>
                <div className="num text-sm font-bold text-[#0020C2]">{formatTL(r.total_new)}</div>
              </div>
              <div className="flex items-center gap-1.5 justify-end">
                <ExportButtons
                  variant="compact"
                  orientation="portrait"
                  testidPrefix={`zam-record-export-${r.id}`}
                  filename={`zam_${r.company_name.toLocaleLowerCase("tr-TR").replace(/[^a-z0-9]/g, "_")}`}
                  title={`${r.company_name} — Yakıt Zam Hesabı`}
                  subtitle={`Yakıt: ${formatTL(r.old_price)} → ${formatTL(r.new_price)} (%${formatNumber(r.increase_pct, 2)}) · Yakıt payı: %${r.fuel_share} · ${trDate(r.old_date)} → ${trDate(r.new_date)}`}
                  sections={[{
                    name: "Güzergahlar", heading: "Güzergah Zam Listesi",
                    columns: [
                      { key: "name", label: "Güzergah" },
                      { key: "price", label: "Günlük Fiyat", format: "money" },
                      { key: "zam", label: "Zam", format: "money" },
                      { key: "new_price", label: "Zamlı Fiyat", format: "money" },
                    ],
                    rows: r.routes,
                  }]}
                />
                <button data-testid={`zam-record-edit-btn-${r.id}`} onClick={() => { setEditing(editing === r.id ? null : r.id); setOpen(r.id); }} title="Düzenle — fiyatlar değişince zam yeniden hesaplanır" className={`p-1.5 ${editing === r.id ? "bg-[#0020C2] text-white" : "hover:bg-[#F5F5F5]"}`}>
                  {editing === r.id ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                </button>
                <button data-testid={`zam-record-toggle-${r.id}`} onClick={() => setOpen(open === r.id ? null : r.id)} className="p-1.5 hover:bg-[#F5F5F5]">
                  <ChevronDown className={`w-4 h-4 transition-transform ${open === r.id ? "rotate-180" : ""}`} />
                </button>
                <button data-testid={`zam-record-delete-${r.id}`} onClick={() => del(r)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {editing === r.id && (
              <ZamRecordEdit
                record={r}
                onSaved={() => { setEditing(null); onDeleted(); }}
                onCancel={() => setEditing(null)}
              />
            )}
            {open === r.id && editing !== r.id && (
              <div className="px-5 pb-4">
                <table className="w-full text-xs border border-[#E5E5E5]">
                  <thead className="bg-[#FAFAFA]">
                    <tr>{["Güzergah", "Günlük Fiyat", "Zam", "Zamlı Fiyat"].map((h, i) => (
                      <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 1 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {r.routes.map((rt, i) => (
                      <tr key={i} className="border-t border-[#F5F5F5]">
                        <td className="px-3 py-2">{rt.name}</td>
                        <td className="px-3 py-2 text-right num">{formatTL(rt.price)}</td>
                        <td className="px-3 py-2 text-right num text-[#DC2626]">+{formatTL(rt.zam)}</td>
                        <td className="px-3 py-2 text-right num font-bold">{formatTL(rt.new_price)}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-[#E5E5E5] bg-[#FAFAFA] font-bold">
                      <td className="px-3 py-2 text-[10px] uppercase tracking-widest">Toplam</td>
                      <td className="px-3 py-2 text-right num">{formatTL(r.total_old)}</td>
                      <td className="px-3 py-2 text-right num text-[#DC2626]">+{formatTL(r.total_zam)}</td>
                      <td className="px-3 py-2 text-right num">{formatTL(r.total_new)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function FuelImpactPage() {
  const [data, setData] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecords = async () => {
    try { const { data } = await api.get("/fuel-impact/zam-records"); setRecords(data); }
    catch { /* sessiz */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [a] = await Promise.all([api.get("/fuel-impact/analysis"), loadRecords()]);
        setData(a.data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-neutral-500 text-sm">Yükleniyor...</div>;

  const monthly = (data?.monthly || []).map(m => ({ ...m, label: monthLabel(m.month) }));
  const latest = data?.latest || {};
  const yoy = data?.yoy;

  return (
    <>
      <PageHeader
        title="Yakıt Zam Etki Analizi"
        subtitle="Yakıt artışını hesaplayın, firma güzergahlarına yansıtın ve firma bazlı kayıt tutun."
        testid="fuel-impact-header"
      />

      {/* Zam Hesaplayıcı */}
      <ZamCalculator onSaved={loadRecords} />

      {/* Kayıtlı zam hesapları (firma bazlı indirme) */}
      <ZamRecords records={records} onDeleted={loadRecords} />

      {/* Alerts */}
      {data?.alerts && data.alerts.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="fuel-alerts">
          {data.alerts.map((a, i) => {
            const st = alertStyles[a.level] || alertStyles.info;
            return (
              <div key={i} className={`flex items-start gap-3 p-4 border-l-4 ${st.border} bg-white`}>
                <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${a.level === "critical" ? "text-[#DC2626]" : "text-[#F59E0B]"}`} />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{a.level === "critical" ? "KRİTİK UYARI" : "UYARI"}</div>
                  <div className="text-sm text-neutral-700 mt-0.5">{a.message}</div>
                </div>
                <span className={`text-xs uppercase tracking-widest font-semibold px-2 py-1 ${st.bg} ${st.text}`}>%{a.delta_pct}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* KPI */}
      {monthly.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <KPICard testid="kpi-tl-per-liter" label="Son Ay ₺ / Litre" value={formatTL(latest.avg_tl_per_liter)}
                   sub={latest.tl_per_liter_change_pct >= 0 ? `+%${latest.tl_per_liter_change_pct} aylık` : `%${latest.tl_per_liter_change_pct} aylık`}
                   accent={latest.tl_per_liter_change_pct >= 5 ? "error" : latest.tl_per_liter_change_pct <= -2 ? "success" : "info"}
                   icon={Fuel} />
          <KPICard testid="kpi-tl-per-km" label="Son Ay ₺ / Km" value={formatTL(latest.avg_tl_per_km)}
                   sub={latest.tl_per_km_change_pct >= 0 ? `+%${latest.tl_per_km_change_pct} aylık` : `%${latest.tl_per_km_change_pct} aylık`}
                   accent={latest.tl_per_km_change_pct >= 5 ? "error" : "info"}
                   icon={Gauge} />
          <KPICard testid="kpi-consumption" label="Son Ay Ort. Tüketim" value={`${formatNumber(latest.avg_consumption, 2)} L/100km`}
                   sub={`Toplam ${formatNumber(latest.total_km)} km`} accent="warning" />
          {yoy && (
            <KPICard testid="kpi-yoy" label="Dönemsel Değişim"
                     value={`+%${yoy.tl_per_liter_change_pct}`}
                     sub={`${monthLabel(yoy.from_month)} → ${monthLabel(yoy.to_month)}`}
                     accent={yoy.tl_per_liter_change_pct >= 20 ? "error" : yoy.tl_per_liter_change_pct >= 10 ? "warning" : "info"}
                     icon={yoy.tl_per_liter_change_pct >= 0 ? TrendingUp : TrendingDown} />
          )}
        </div>
      )}

      {/* Grafikler */}
      {monthly.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="chart-tl-per-liter">
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Yakıt Fiyatı Trendi</div>
            <div className="text-lg font-bold font-heading mt-1 mb-4">Aylık Ortalama ₺ / Litre</div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatTL(v)} />
                  <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="avg_tl_per_liter" name="₺ / Litre" stroke="#DC2626" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid="chart-tl-per-km">
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Km Başına Yakıt Maliyeti</div>
            <div className="text-lg font-bold font-heading mt-1 mb-4">Aylık ₺ / Km Trendi</div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatTL(v)} />
                  <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="avg_tl_per_km" name="₺ / Km" radius={[4, 4, 0, 0]}>
                    {monthly.map((m, i) => {
                      const c = m.tl_per_km_change_pct >= 10 ? "#DC2626" : m.tl_per_km_change_pct >= 5 ? "#F59E0B" : m.tl_per_km_change_pct >= 0 ? "#0020C2" : "#16A34A";
                      return <Cell key={i} fill={c} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
